import './styles.css';
import { Game, MAPS, DIFFICULTIES, BIOMES, generateProceduralMap } from './game/Game.js';
import { looplay } from './looplay/LooplayBridge.js';

const app = document.querySelector('#app');
app.innerHTML = `
  <main class="game-shell" aria-label="Windshot PvE">
    <section class="topbar" aria-label="Battle status">
      <div class="hud-card player-card">
        <div class="hud-title">
          <span class="hud-tag"><span class="actor-dot player-dot"></span>Player</span>
          <span class="hud-val" id="playerHp">100 / 100</span>
        </div>
        <div class="hp-track"><i id="playerHpFill" class="player-fill"></i></div>
      </div>

      <div class="wind-card" aria-label="Wind speed">
        <div class="wind-arrow-wrap">
          <div class="wind-arrow" id="windArrow">➔</div>
        </div>
        <div class="wind-meta">
          <span class="wind-tag">WIND</span>
          <strong id="wind">0.0</strong>
        </div>
      </div>

      <div class="hud-card enemy-card">
        <div class="hud-title">
          <span class="hud-tag"><span class="actor-dot enemy-dot"></span>Target</span>
          <span class="hud-val" id="enemyHp">—</span>
        </div>
        <div class="hp-track"><i id="enemyHpFill" class="enemy-fill"></i></div>
      </div>
    </section>

    <section class="canvas-wrap">
      <canvas id="game" width="960" height="540" aria-label="Turn-based artillery battlefield"></canvas>
      <div class="stage-chip" id="stageChip">Select a map to begin</div>
      <div class="camera-controls" aria-label="Camera controls">
        <button id="zoomInBtn" class="cam-btn" type="button" title="Zoom in" aria-label="Zoom in">🔍+</button>
        <button id="zoomOutBtn" class="cam-btn" type="button" title="Zoom out" aria-label="Zoom out">🔍−</button>
        <button id="camFocusBtn" class="cam-btn" type="button" title="Focus player" aria-label="Focus player">🎯</button>
      </div>
      <div class="cam-hint" id="camHint">Drag to view map • Pinch / buttons to zoom</div>
      <div id="overlay" class="overlay">
        <div class="panel" id="panel"></div>
      </div>
    </section>

    <section id="controls" class="controls hidden" aria-label="Controls">
      <div class="control-head">
        <span class="map-label" id="mapName">—</span>

        <div class="move-controls" aria-label="Movement controls">
          <button id="moveLeft" class="move-btn" type="button" title="Move Left (A / ◀)" aria-label="Move Left">◀</button>
          <div class="stamina-badge" title="Stamina">
            <div class="stamina-meta">
              <span class="stamina-icon">⚡</span>
              <strong id="staminaVal">100</strong>
            </div>
            <div class="stamina-track"><i id="staminaFill"></i></div>
          </div>
          <button id="moveRight" class="move-btn" type="button" title="Move Right (D / ▶)" aria-label="Move Right">▶</button>
        </div>

        <span id="turn" class="turn-badge">Your Turn</span>
      </div>

      <div class="aim-row">
        <div class="aim-controls">
          <div class="control-block angle-block">
            <label for="angle"><span>Angle</span><strong id="angleValue">45°</strong></label>
            <input id="angle" type="range" min="10" max="85" value="45" step="1" />
          </div>
          <div class="control-block power-block">
            <label for="power"><span>Power</span><strong id="powerValue">65%</strong></label>
            <input id="power" type="range" min="15" max="100" value="65" step="1" />
          </div>
        </div>
        <button id="fire" class="fire" type="button">
          <span class="fire-title">FIRE 💥</span>
          <small>TAP TO FIRE</small>
        </button>
      </div>

      <div class="items">
        <button id="heal" class="item heal-item" type="button" title="Heal">❤️<span>2</span></button>
        <button id="double" class="item double-item" type="button" title="Double Shot">✨<span>1</span></button>
        <button id="shield" class="item shield-item" type="button" title="Shield">🛡️<span>1</span></button>
        <button id="menuBtn" class="menu-btn" type="button">☰ Map</button>
      </div>
    </section>
  </main>
`;

const ui = {
  overlay: document.querySelector('#overlay'),
  panel: document.querySelector('#panel'),
  controls: document.querySelector('#controls'),
  playerHp: document.querySelector('#playerHp'),
  playerHpFill: document.querySelector('#playerHpFill'),
  enemyHp: document.querySelector('#enemyHp'),
  enemyHpFill: document.querySelector('#enemyHpFill'),
  wind: document.querySelector('#wind'),
  windArrow: document.querySelector('#windArrow'),
  stageChip: document.querySelector('#stageChip'),
  mapName: document.querySelector('#mapName'),
  moveLeft: document.querySelector('#moveLeft'),
  moveRight: document.querySelector('#moveRight'),
  staminaVal: document.querySelector('#staminaVal'),
  staminaFill: document.querySelector('#staminaFill'),
  angle: document.querySelector('#angle'),
  angleValue: document.querySelector('#angleValue'),
  power: document.querySelector('#power'),
  powerValue: document.querySelector('#powerValue'),
  fire: document.querySelector('#fire'),
  heal: document.querySelector('#heal'),
  double: document.querySelector('#double'),
  shield: document.querySelector('#shield'),
  menuBtn: document.querySelector('#menuBtn'),
  turn: document.querySelector('#turn'),
  zoomInBtn: document.querySelector('#zoomInBtn'),
  zoomOutBtn: document.querySelector('#zoomOutBtn'),
  camFocusBtn: document.querySelector('#camFocusBtn'),
  camHint: document.querySelector('#camHint')
};

const game = new Game(document.querySelector('#game'), ui);
window.game = game;

function starsFor(map) {
  const n = game.save.stars[map.id] || 0;
  return '★'.repeat(n) + '☆'.repeat(3 - n);
}

function renderMenu() {
  const currentMap = game.selectedMap;
  const currentDiff = DIFFICULTIES[game.difficultyKey] || DIFFICULTIES.normal;

  ui.stageChip.textContent = `${currentMap.icon} ${currentMap.name} · ${currentDiff.name}`;
  ui.panel.innerHTML = `
    <div class="menu-brand">
      <img src="./logo.png" alt="Windshot PvE" class="menu-logo" />
    </div>
    <p class="subtitle">Blast destructible terrain, read wind physics, and calculate trajectories to conquer PvE stages!</p>

    <!-- Selected Map Preview & Reroll -->
    <div class="selected-map-card">
      <div class="map-preview-header">
        <span class="map-icon-large">${currentMap.icon}</span>
        <div class="map-preview-info">
          <div class="map-preview-tag">${currentMap.isProcedural ? '🎲 PROCEDURAL BATTLEFIELD' : 'CAMPAIGN STAGE'}</div>
          <strong class="map-preview-name">${currentMap.name}</strong>
          <div class="map-preview-meta">
            <span>💨 Wind x${currentMap.wind}</span>
            <span>🪐 Grav ${currentMap.gravity || 1.0}G</span>
            <span>👾 ${currentMap.enemies} Bots</span>
            ${currentMap.boss ? '<span class="boss-tag">👑 BOSS</span>' : ''}
          </div>
        </div>
        <button id="rerollBtn" class="reroll-btn" type="button" title="Reroll terrain for this map">
          🔄 Reroll
        </button>
      </div>
    </div>

    <div class="section-label">Select Battlefield or Roll Random</div>
    <div class="map-grid" id="mapGrid">
      <button type="button" class="map-btn random-btn ${currentMap.isProcedural ? 'selected' : ''}" id="randomMapBtn">
        <span class="map-icon">🎲</span>
        <strong>Random Map</strong>
        <div class="map-stars">✨ Infinite</div>
      </button>
    </div>

    <!-- Difficulty Selector with Description -->
    <div class="difficulty-section">
      <div class="difficulty-row">
        <label for="difficulty" class="diff-label">Difficulty:</label>
        <select id="difficulty" class="difficulty" aria-label="Difficulty">
          ${Object.entries(DIFFICULTIES).map(([k, v]) => `
            <option value="${k}" ${k === game.difficultyKey ? 'selected' : ''}>
              ${v.badge || v.name}
            </option>
          `).join('')}
        </select>
      </div>
      <p class="diff-desc" id="diffDesc">${currentDiff.description}</p>
    </div>

    <div class="row">
      <button id="startBtn" class="primary start-battle-btn" type="button">START BATTLE 🚀</button>
    </div>
  `;

  const grid = ui.panel.querySelector('#mapGrid');
  MAPS.forEach((map, index) => {
    const locked = index > game.save.unlocked;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `map-btn ${!currentMap.isProcedural && currentMap.id === map.id ? 'selected' : ''} ${locked ? 'locked' : ''}`;
    button.disabled = locked;
    button.innerHTML = `<span class="map-icon">${map.icon}</span><strong>${map.name}</strong><div class="map-stars">${locked ? '🔒' : starsFor(map)}</div>`;
    button.addEventListener('click', () => {
      game.setMap(map);
      renderMenu();
    });
    grid.appendChild(button);
  });

  ui.panel.querySelector('#randomMapBtn').addEventListener('click', () => {
    game.setRandomMap('random');
    renderMenu();
  });

  ui.panel.querySelector('#rerollBtn').addEventListener('click', () => {
    game.rerollCurrentMap();
    renderMenu();
  });

  const diffSelect = ui.panel.querySelector('#difficulty');
  diffSelect.addEventListener('change', (event) => {
    game.setDifficulty(event.target.value);
    const d = DIFFICULTIES[event.target.value];
    if (d) {
      ui.panel.querySelector('#diffDesc').textContent = d.description;
    }
  });

  ui.panel.querySelector('#startBtn').addEventListener('click', () => game.start());
}

function renderResult({ won, score, stars, map }) {
  ui.stageChip.textContent = won ? `🏆 ${map.name} Completed` : `💀 ${map.name} · Try Again`;
  ui.panel.innerHTML = `
    <div class="result-brand">
      <span class="result-badge ${won ? 'win' : 'lose'}">${won ? 'VICTORY' : 'DEFEAT'}</span>
      <h2>${won ? '🏆 Mission Accomplished!' : '💀 Battle Defeated'}</h2>
    </div>
    <p>${won ? `You conquered ${map.name} with surgical precision!` : 'Adjust your angle and power, read the wind, and use destructible terrain to your advantage.'}</p>
    <div class="result-stars">${won ? '★'.repeat(stars) + '☆'.repeat(3 - stars) : '☆☆☆'}</div>
    <p class="result-score"><strong>Score:</strong> ${score.toLocaleString('en-US')}</p>
    <div class="row">
      ${!won && looplay.canRequestRewardedAd ? '<button id="revive" class="primary" type="button">▶ Watch ad to revive · 50% HP</button>' : ''}
      <button id="again" class="primary" type="button">Play Again ⚔️</button>
      <button id="nextRandom" class="primary random-next-btn" type="button">🎲 Random Map</button>
      <button id="maps" class="menu-btn" type="button">Menu ☰</button>
    </div>
  `;
  const reviveButton = ui.panel.querySelector('#revive');
  reviveButton?.addEventListener('click', async () => {
    reviveButton.disabled = true;
    reviveButton.textContent = 'Loading ad…';
    const watched = await looplay.requestRewardedAd();
    if (watched) {
      game.reviveAfterReward();
      return;
    }

    reviveButton.disabled = false;
    reviveButton.textContent = 'Ad unavailable · Try again';
  });
  ui.panel.querySelector('#again').addEventListener('click', () => game.start());
  ui.panel.querySelector('#nextRandom').addEventListener('click', () => {
    game.setRandomMap('random');
    game.start();
  });
  ui.panel.querySelector('#maps').addEventListener('click', () => game.showMenu());
}

ui.showMenu = renderMenu;
ui.showResult = renderResult;

void looplay.init();
window.addEventListener('pagehide', () => looplay.stop({ reason: 'pagehide' }));
renderMenu();
